/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import { isActionSkill } from '@ibm-watson/shared/modules/actions/util/skill';
import { getAccessPrefixedUrl } from '../../../ia/util/access-control';
import { endpoints } from '../../../ia/util/config';
import {
  transformParameters,
  serializeParameters as paramsSerializer
} from '../../../ia/util/rest';
import { validation } from '../../../ia/util/validation';
import { workspaceFactory } from './factories';

const WORKSPACES_ENDPOINT = 'assets/workspaces';
const SAMPLE_WORKSPACES_ENDPOINT = WORKSPACES_ENDPOINT + '/samples';

function skillsEndpoint(useNewIAUpdatedAccess: boolean, assistantId: string) {
  return urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(useNewIAUpdatedAccess, assistantId, '/skills')
  );
}

function skillEndpoint(
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  return urlJoin(skillsEndpoint(useNewIAUpdatedAccess, assistantId), skillId);
}

export function getWorkspace(workspace_id, options, skillId) {
  const validationErrors = validation.validateGuid(skillId);
  if (validationErrors) {
    return Promise.reject({ data: { error: validationErrors } });
  }
  const config = {
    params: transformParameters(options),
    paramsSerializer
  };
  const url = skillEndpoint(skillId, false, '');
  return axios
    .get(url, config)
    .then(response => workspaceFactory(response.data))
    .catch(error => Promise.reject(error));
}

export function getStarterWorkspace(skillType, language) {
  const fileName = `${language}-${skillType}`;

  return axios
    .get(`assets/workspaces/starters/${fileName}`, {})
    .then(response => workspaceFactory(response.data))
    .catch(error => Promise.reject(error));
}

export function getSampleWorkspace(workspaceId) {
  const endpoint = [SAMPLE_WORKSPACES_ENDPOINT, workspaceId + '.json'].join(
    '/'
  );
  return axios
    .get(endpoint)
    .then(response => {
      if (isActionSkill(response.data)) {
        return response.data;
      }
      response.data.is_sample = true;
      response.data.counts = {
        intent: response.data.intents.length,
        entity: response.data.entities.length,
        node: response.data.dialog_nodes.length
      };
      return workspaceFactory(response.data);
    })
    .catch(error => Promise.reject(error));
}

export function updateWorkspace(
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  params: any,
  options: any
) {
  let validationErrors = [];
  const guidErrors = validation.validateGuid(skillId);
  if (guidErrors) {
    validationErrors = validationErrors.concat(guidErrors);
  }
  const workspaceErrors = validation.validateWorkspace(params);
  if (workspaceErrors) {
    validationErrors = validationErrors.concat(workspaceErrors);
  }
  if (validationErrors.length) {
    return Promise.reject({ data: { error: validationErrors } });
  }

  const data = _.pick(params, [
    'name',
    'description',
    'language',
    'metadata',
    'dialog_nodes',
    'intents',
    'entities',
    'counterexamples',
    'system_settings',
    'imports',
    'learning_opt_out',
    'webhooks'
  ]);
  const config = options ? _.pick(options, ['append', 'include_audit']) : {};
  const url = skillEndpoint(skillId, useNewIAUpdatedAccess, assistantId);

  const candidates = [
    'intents',
    'entities',
    'dialog_nodes',
    'counterexamples',
    'system_settings',
    'learning_opt_out',
    'status',
    'metadata'
  ];
  const intersections = _.intersection(Object.keys(data), candidates);
  if (intersections.length > 0) {
    const workspace = {};
    intersections.forEach(key => {
      workspace[key] = data[key];
      delete data[key];
    });
    data['workspace'] = workspace;
  }
  return axios
    .post(url, data, { params: config })
    .then(response => workspaceFactory(response.data))
    .catch(error => Promise.reject(error));
}
